import { type RJSFSchema, type UiSchema } from '@rjsf/utils'
import { type ParametersFormContext } from './formContext'
import { isPlainObject, type JsonObject } from './mergePatch'
import { isArraySchema, objectItemsOf } from './rjsf/arrayModel'
import { warnZocksUi, type ZocksUi, type ZocksUiHeading, zocksUiOf } from './zocksUi'

const EXPANDABLE_ITEM = { 'ui:zocksItemLayout': 'expandable', 'ui:compact': true }
const ROW_FIELD = { 'ui:labelLayout': 'hidden', 'ui:compact': true, 'ui:listItem': true }

const mergeUiSchemaNodes = (a: JsonObject, b: JsonObject): JsonObject =>
  Object.entries(b).reduce<JsonObject>(
    (result, [key, value]) => {
      const existing = result[key]
      return { ...result, [key]: isPlainObject(existing) && isPlainObject(value) ? mergeUiSchemaNodes(existing, value) : value }
    },
    { ...a },
  )

const ownPropertyNames = (schema: JsonObject): string[] => (isPlainObject(schema.properties) ? Object.keys(schema.properties) : [])

export interface ParameterSectionConfig extends ZocksUiHeading {
  start: string
}

interface OrderLayout {
  order?: string[]
  sections?: ParameterSectionConfig[]
}

const branchPropertyNames = (schema: JsonObject): string[] =>
  branchesOf(schema).flatMap((branch) => (isPlainObject(branch) ? [...ownPropertyNames(branch), ...branchPropertyNames(branch)] : []))

const orderLayoutOf = (schema: JsonObject, order: ZocksUi['order']): OrderLayout => {
  const properties = [...ownPropertyNames(schema), ...branchPropertyNames(schema)].filter((name, index, all) => all.indexOf(name) === index)
  const entries = order ?? []
  const names = entries.filter((entry): entry is string => typeof entry === 'string')
  const unknown = names.filter((name) => !properties.includes(name))
  if (unknown.length > 0) {
    warnZocksUi(`order names unknown properties: ${unknown.join(', ')}`)
  }
  const listed = names.filter((name) => properties.includes(name))
  const sections = entries.flatMap((entry, index): ParameterSectionConfig[] => {
    if (typeof entry === 'string') {
      return []
    }
    const next = entries.slice(index + 1).find((later) => typeof later !== 'string' || listed.includes(later))
    if (typeof next !== 'string') {
      warnZocksUi(`heading "${entry.title}" ignored: no property follows it`)
      return []
    }
    return [{ ...entry, start: next }]
  })
  const unlisted = properties.filter((name) => !listed.includes(name))
  if (sections.length > 0 && unlisted.length > 0) {
    warnZocksUi(`order has headings but doesn't list: ${unlisted.join(', ')}`)
  }
  return {
    order: listed.length > 0 ? [...listed, '*'] : undefined,
    sections: sections.length > 0 ? sections : undefined,
  }
}

const uniqueFieldOf = (schema: JsonObject, uniqueField: string | undefined): string | undefined => {
  if (uniqueField === undefined) {
    return undefined
  }
  const items = objectItemsOf(schema)
  if (items && ownPropertyNames(items).includes(uniqueField)) {
    return uniqueField
  }
  warnZocksUi(`uniqueField "${uniqueField}" is not a property of the list's items`)
  return undefined
}

const isFreeTextString = (schema: JsonObject): boolean =>
  schema.type === 'string' && !['enum', 'const', 'oneOf', 'anyOf', 'zocksReference'].some((keyword) => keyword in schema)

const widgetOf = (schema: JsonObject, widget: ZocksUi['widget']): ZocksUi['widget'] => {
  if (widget === undefined || isFreeTextString(schema)) {
    return widget
  }
  warnZocksUi(`widget "${widget}" ignored: it needs a string field without fixed options`)
  return undefined
}

const zocksUiNode = (schema: JsonObject): JsonObject => {
  const { widget, order, uniqueField } = zocksUiOf(schema)
  const layout = orderLayoutOf(schema, order)
  return Object.fromEntries(
    Object.entries({
      'ui:widget': widgetOf(schema, widget),
      'ui:order': layout.order,
      'ui:zocksSections': layout.sections,
      'ui:uniqueField': uniqueFieldOf(schema, uniqueField),
    }).filter(([, value]) => value !== undefined),
  )
}

const withoutLayout = (node: JsonObject): JsonObject =>
  Object.fromEntries(
    Object.entries(node)
      .filter(([key]) => key !== 'ui:order' && key !== 'ui:zocksSections')
      .map(([key, value]) => [key, isPlainObject(value) ? withoutLayout(value) : value] as const)
      .filter(([, value]) => !isPlainObject(value) || Object.keys(value).length > 0),
  )

const childNodes = (schema: JsonObject): JsonObject => {
  const properties = isPlainObject(schema.properties) ? Object.entries(schema.properties) : []
  const items = schema.items === undefined ? [] : [['items', schema.items] as const]
  return Object.fromEntries(
    [...properties, ...items].map(([key, child]) => [key, buildUiSchema(child)] as const).filter(([, node]) => Object.keys(node).length > 0),
  )
}

const branchesOf = (schema: JsonObject): unknown[] => [
  ...(['then', 'else'] as const).flatMap((key) => (schema[key] === undefined ? [] : [schema[key]])),
  ...(['allOf', 'oneOf', 'anyOf'] as const).flatMap((key) => {
    const branches = schema[key]
    return Array.isArray(branches) ? branches : []
  }),
]

export const buildUiSchema = (schema: unknown): UiSchema<JsonObject, RJSFSchema, ParametersFormContext> => {
  if (!isPlainObject(schema)) {
    return {}
  }
  const node: JsonObject = {
    ...zocksUiNode(schema),
    ...(isPlainObject(schema.zocksReference) ? { 'ui:widget': 'zocksReference', 'ui:zocksReference': schema.zocksReference } : {}),
    ...childNodes(schema),
  }
  const withItems = isArraySchema(schema)
    ? { ...node, items: mergeUiSchemaNodes(isPlainObject(node.items) ? node.items : {}, objectItemsOf(schema) ? EXPANDABLE_ITEM : ROW_FIELD) }
    : node
  return branchesOf(schema).reduce<JsonObject>((merged, branch) => mergeUiSchemaNodes(merged, withoutLayout(buildUiSchema(branch))), withItems)
}
